<template>
  <div class="theme-toggle">
    <div class="theme-toggle__label">
      <v-icon :icon="currentIcon" size="20" />
      <span>{{ t('core.common.theme.title') }}</span>
    </div>
    <div class="theme-toggle__control">
      <v-tooltip v-for="option in options" :key="option.value" :text="option.label" location="bottom">
        <template #activator="{ props }">
          <button
            v-bind="props"
            :class="['theme-toggle__option', { 'theme-toggle__option--active': isActive(option.value) }]"
            type="button"
            @click="setTheme(option.value)"
          >
            <v-icon :icon="option.icon" size="16" />
          </button>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiBrightness6, mdiMoonWaxingCrescent, mdiWhiteBalanceSunny } from '@mdi/js'
import { useI18n } from 'vue-i18n'
import { useThemeStore } from '../../stores/useThemeStore'

const { t } = useI18n()
const themeStore = useThemeStore()

const options = computed(() => [
  { value: 'light', icon: mdiWhiteBalanceSunny, label: t('core.common.theme.light') },
  { value: 'dark', icon: mdiMoonWaxingCrescent, label: t('core.common.theme.dark') },
  { value: 'system', icon: mdiBrightness6, label: t('core.common.theme.system') },
])

const currentIcon = computed(() => {
  const mode = themeStore.themeMode
  if (mode === 'light') return mdiWhiteBalanceSunny
  if (mode === 'dark') return mdiMoonWaxingCrescent
  return mdiBrightness6
})

function isActive(value: string) {
  return themeStore.themeMode === value
}

function setTheme(value: string) {
  themeStore.setTheme(value as 'light' | 'dark' | 'system')
}
</script>

<style lang="scss">
@use '@/styles/design-tokens' as *;

.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-sm $space-md;
  margin: 0 $space-xs;
  border-radius: $radius-md;
  @include transition-fast;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }

  &__label {
    display: flex;
    align-items: center;
    gap: $space-sm;
    font-size: $text-md;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
  }

  &__control {
    display: flex;
    align-items: center;
    background: rgba(var(--v-theme-on-surface), 0.06);
    border-radius: $radius-full;
    padding: 2px;
    gap: 2px;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 26px;
    border-radius: $radius-full;
    border: none;
    background: transparent;
    cursor: pointer;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    @include transition-fast;

    &--active {
      background: rgb(var(--v-theme-surface));
      color: rgb(var(--v-theme-primary));
      box-shadow: $shadow-xs;
    }

    &:hover:not(&--active) {
      color: rgba(var(--v-theme-on-surface), $opacity-primary);
    }
  }
}
</style>
